import React from 'react';

const InstagramGlyph = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3a5 5 0 110 10 5 5 0 010-10zm0 2.2a2.8 2.8 0 100 5.6 2.8 2.8 0 000-5.6zM18 6.5a1 1 0 110 2 1 1 0 010-2z" />
  </svg>
);
const VideoBadge = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
);
const CarouselBadge = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 7h12a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2zm-2-4h12v2H5z" />
  </svg>
);

/**
 * "Follow Our Story" Instagram grid (same system as PS 96). Photos are copied
 * into the site by scripts/sync-instagram.mjs (run daily, see INSTAGRAM_FEED.md),
 * so visitors load nothing from Instagram until they choose to open a post.
 */
const InstagramFeed = ({ feed }) => {
  // whole rows of three only
  const posts = feed.posts.slice(0, Math.floor(feed.posts.length / 3) * 3);

  return (
    <section className="section-padding bg-white" aria-labelledby="instagram-heading" data-animate>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="w-8 h-px bg-brand-accent"></div>
            <span className="text-brand-accent-dark font-medium tracking-wide">On Instagram</span>
            <div className="w-8 h-px bg-brand-accent"></div>
          </div>
          <h2 id="instagram-heading" className="text-display text-heading font-display mb-4">Follow Our Story</h2>
          <p className="text-lg text-body">See daily moments from our classrooms, hallways, and community.</p>
          <a
            href={feed.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-3 btn-primary"
          >
            <InstagramGlyph />
            Follow @{feed.handle}
            <span className="sr-only"> on Instagram (opens in a new tab)</span>
          </a>
        </div>

        <ul className="grid grid-cols-3 gap-1.5 sm:gap-2">
          {posts.map((post) => (
            <li key={post.id} className="relative aspect-square overflow-hidden bg-brand-light rounded-sm">
              <a
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="group block w-full h-full focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-brand-yellow"
              >
                <img
                  src={post.image}
                  alt={post.alt}
                  width="640"
                  height="640"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none"
                />
                <span className="sr-only"> — view on Instagram{post.type === 'video' ? ' (video)' : post.type === 'carousel' ? ' (multiple photos)' : ''} (opens in a new tab)</span>
                {post.type !== 'image' && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-navy/85 text-white">
                    {post.type === 'video' ? <VideoBadge /> : <CarouselBadge />}
                  </span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/**
 * The gold "Follow Our Story" strip (same as PS 96's). Shown in the grid's
 * place until the daily sync has brought in posts.
 */
export const InstagramStrip = ({ feed, facebookUrl }) => (
  <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-yellow" aria-labelledby="instagram-strip-heading">
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-8 text-center sm:text-left">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-brand-navy mb-1">Stay Connected</p>
          <h2 id="instagram-strip-heading" className="text-3xl md:text-4xl font-display text-brand-navy">Follow Our Story</h2>
          <p className="text-brand-navy font-medium mt-2 max-w-sm">
            See daily moments from our classrooms, hallways, and community.
          </p>
        </div>
        <div className="flex flex-col gap-3 items-center sm:items-end">
          <a
            href={feed.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-7 py-4 bg-brand-navy text-white font-semibold rounded-2xl shadow-lg hover:bg-brand-primary hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 motion-reduce:transition-none"
          >
            <InstagramGlyph />
            @{feed.handle} on Instagram
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="py-1 text-sm font-semibold text-brand-navy hover:underline"
          >
            Also on Facebook → 108soa<span className="sr-only"> (opens in a new tab)</span>
          </a>
        </div>
      </div>
    </div>
  </section>
);

export default InstagramFeed;
